import i18n from 'i18next'
import ICU from 'i18next-icu'
import { initReactI18next } from 'react-i18next'
import axios from 'axios'
import posthog from 'posthog-js'
import axiosInstance from '../axios/axiosInstance'
import { localeBackend } from './backend'
import { languageSuperProperties, noteRequestedLanguage } from './current'
import { detectInitialLanguage, InterfaceLanguage, isPseudoAllowed, writeStoredLanguage } from './detect'
import { followDocumentTitle } from './documentTitle'
import { ensureInterfaceFont } from './fonts'
import { highlightPostProcessor, highlightTarget, startHighlighting } from './highlight'
import { DEFAULT_LANGUAGE, DEFAULT_NAMESPACE, PSEUDO_LOCALE, normalizeLanguage } from './languages'
import { missingKeyPostProcessor } from './missingKeys'
import { baseInitOptions } from './options'
import { pseudoPostProcessor } from './pseudo'

export { currentLanguage, currentLocale } from './current'
export * from './languages'

/** The language the API should answer in. The pseudo-locale asks for English. */
const apiLanguage = (language: InterfaceLanguage) => (language === PSEUDO_LOCALE ? DEFAULT_LANGUAGE : language)

/**
 * Everything outside i18next that follows the interface language: <html lang>, the
 * Accept-Language header on API calls, the Japanese or Korean font, and PostHog's `ui_language`
 * (also registered from PostHog's `loaded` hook in utils/config.ts, for the first page view).
 *
 * moment stays in English on purpose: it would need its locale files bundled. Format dates for
 * people with the helpers in i18n/format.ts (Intl), and keep moment for date maths.
 */
const applyLanguageEverywhere = (language: InterfaceLanguage) => {
  noteRequestedLanguage(language)
  if (typeof document !== 'undefined') document.documentElement.lang = language
  const header = apiLanguage(language)
  axiosInstance.defaults.headers.common['Accept-Language'] = header
  axios.defaults.headers.common['Accept-Language'] = header
  ensureInterfaceFont(language)
  if ((posthog as any).__loaded) posthog.register(languageSuperProperties())
}

let initPromise: Promise<unknown> | null = null

/**
 * Starts i18next in the detected language (localStorage, then the browser, then English). The
 * profile's `native_language` takes over once it loads, see i18n/profileLanguage.ts.
 */
export const initI18n = () => {
  if (initPromise) return initPromise
  const allowPseudo = isPseudoAllowed()
  const language = detectInitialLanguage()
  applyLanguageEverywhere(language)
  // Opened from the translation review dashboard: outline one string (highlight.ts).
  const highlight = highlightTarget()
  const postProcess = [
    ...(process.env.NODE_ENV === 'test' ? [] : ['reportMissing']),
    ...(allowPseudo ? ['pseudo'] : []),
    ...(highlight ? ['i18nHighlight'] : []),
  ]
  i18n.use(localeBackend).use(ICU).use(initReactI18next).use(missingKeyPostProcessor)
  if (allowPseudo) i18n.use(pseudoPostProcessor)
  if (highlight) {
    i18n.use(highlightPostProcessor(highlight))
    startHighlighting()
  }
  // The tab title follows the interface language (index.html keeps the English for crawlers).
  followDocumentTitle(i18n)
  initPromise = i18n.init({
    ...baseInitOptions({ allowPseudo }),
    // `errors` loads up front with `common`: apiErrorMessage (apiError.ts) is called from actions
    // outside React, where nothing would load it before a server error needs translating.
    ns: [DEFAULT_NAMESPACE, 'errors'],
    lng: language,
    postProcess: postProcess.length ? postProcess : false,
    postProcessPassResolved: true,
  })
  return initPromise
}

/**
 * Switches the interface language now: i18next (after the new language's strings have loaded),
 * localStorage, <html lang>, Accept-Language, fonts and PostHog. It does not save to the profile;
 * use saveLanguage in i18n/profileLanguage.ts for that.
 */
export const setLanguage = async (requested: string) => {
  const language: InterfaceLanguage =
    isPseudoAllowed() && requested === PSEUDO_LOCALE ? PSEUDO_LOCALE : normalizeLanguage(requested)
  writeStoredLanguage(language)
  applyLanguageEverywhere(language)
  if (i18n.language !== language) await i18n.changeLanguage(language)
  return language
}

export default i18n
